/**
 * Shared viewport scroll lock for modals/overlays.
 *
 * IMPORTANT: index.css sets `html { overflow-x: clip }`. Per the CSS spec,
 * once <html> has non-visible overflow on any axis, the <body>'s overflow
 * no longer propagates to the viewport, so the old per-modal
 * `document.body.style.overflow = 'hidden'` locks silently did nothing
 * (root cause of the recurring "background scrolls behind modal" bug).
 * The viewport is scrolled via <html>, so we must lock documentElement.
 *
 * Ref-counted so stacked modals (e.g. a dialog opened from another modal)
 * don't unlock early when the inner one closes.
 */
import { useEffect } from 'react';

let lockCount = 0;
let saved = null;

export function lockScroll() {
  if (++lockCount > 1) return;
  const html = document.documentElement;
  const body = document.body;
  saved = {
    htmlOverflowY: html.style.overflowY,
    bodyOverflow: body.style.overflow,
    bodyPaddingRight: body.style.paddingRight,
  };
  const scrollbarWidth = window.innerWidth - html.clientWidth;
  html.style.overflowY = 'hidden';
  body.style.overflow = 'hidden';
  if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
}

export function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0 || !saved) return;
  const html = document.documentElement;
  const body = document.body;
  html.style.overflowY = saved.htmlOverflowY;
  body.style.overflow = saved.bodyOverflow;
  body.style.paddingRight = saved.bodyPaddingRight;
  saved = null;
}

export default function useScrollLock(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    lockScroll();
    return unlockScroll;
  }, [active]);
}
